import {
  Box, Heading, Text, VStack, HStack, FormControl, FormLabel,
  Input, Button, useToast, Spinner, Badge, Textarea,
  Card, CardBody, IconButton, useDisclosure, Stack, Checkbox,
  Alert, AlertIcon, AlertTitle, AlertDescription, Container,
  Table, Tbody, Tr, Td, Th, Thead, Flex, Tooltip, useClipboard,
  Modal, ModalOverlay, ModalContent, ModalBody, ModalHeader,
  ModalFooter, ModalCloseButton, Select, Wrap, WrapItem,
  NumberInput, NumberInputField,
  SimpleGrid, Stat, StatNumber, StatLabel, Divider
} from "@chakra-ui/react";
import {
  CheckIcon, CloseIcon, WarningIcon, EditIcon, CopyIcon,
  AddIcon, DeleteIcon, RepeatIcon
} from "@chakra-ui/icons";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  buscarListaComprasPorHash,
  adicionarRespostaItem,
  removerRespostaFornecedor,
  declararFuroEstoqueItem,
  desfazerFuroEstoqueItem,
  adicionarDivergencia,
  removerDivergencia,
  atualizarDivergencia,
  informarPedidoFormalizado,
} from "../services/api";
import ProdutoImagem from "../components/ProdutoImagem";

const TIPOS_DIVERGENCIA_CADASTRAL = [
  { value: 'marca_diferente',     label: 'Marca diferente' },
  { value: 'imagem_incorreta',    label: 'Imagem incorreta' },
  { value: 'codigo_diferente',    label: 'Código diferente' },
  { value: 'descricao_incorreta', label: 'Descrição incorreta' },
  { value: 'outro',               label: 'Outro' },
];

const tipoDivergenciaLabel = (tipo) => {
  const map = {
    marca_diferente: 'Marca diferente',
    valor_diferente: 'Preço diferente',
    quantidade_diferente: 'Quantidade diferente',
    imagem_incorreta: 'Imagem incorreta',
    codigo_diferente: 'Código diferente',
    descricao_incorreta: 'Descrição incorreta',
    outro: 'Outro',
  };
  return map[tipo] || tipo;
};

const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return "";
  const numero = typeof valor === 'string' ? valor.replace(/\D/g, "") : valor.toString();
  if (!numero) return "";
  const valorNumerico = parseFloat(numero) / 100;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorNumerico);
};

const formatarMoedaDecimal = (valorDecimal) => {
  if (valorDecimal === null || valorDecimal === undefined || valorDecimal === '') return "";
  const valorNumerico = parseFloat(valorDecimal);
  if (isNaN(valorNumerico)) return "";
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorNumerico);
};

const moedaParaDecimal = (valorFormatado) => {
  if (!valorFormatado) return null;
  const numero = (valorFormatado + '').replace(/\D/g, "");
  if (!numero) return null;
  return (parseFloat(numero) / 100).toFixed(2);
};

const aproximadamenteIgual = (a, b) => {
  if (a == null || b == null) return false;
  return Math.abs(parseFloat(a) - parseFloat(b)) < 0.005;
};

// Retorna a resposta aprovada (ou parcialmente aprovada) de um item, se houver.
// Em listas aprovadas/finalizadas e o que o fornecedor precisa formalizar.
const respostaAprovadaDoItem = (item) => {
  const respostas = item?.respostas || [];
  return respostas.find((r) => r.status_aprovacao === 'aprovado')
    || respostas.find((r) => r.status_aprovacao === 'parcialmente_aprovado')
    || null;
};

// Quantidade efetiva aprovada (parcial < disponivel < solicitada).
const qtdAprovadaDoItem = (item) => {
  const resp = respostaAprovadaDoItem(item);
  if (!resp) return 0;
  const aprovada = parseInt(resp.quantidade_aprovada, 10);
  if (!isNaN(aprovada) && aprovada > 0) return aprovada;
  const disponivel = parseInt(resp.quantidade_disponivel, 10);
  return !isNaN(disponivel) ? disponivel : 0;
};

const precoAprovadoDoItem = (item) => {
  const resp = respostaAprovadaDoItem(item);
  if (!resp) return null;
  return parseFloat(resp.preco_unitario);
};

const subtotalAprovadoDoItem = (item) => {
  const preco = precoAprovadoDoItem(item);
  const qtd = qtdAprovadaDoItem(item);
  if (preco == null || qtd === 0) return 0;
  return preco * qtd;
};

// Gera linha de texto pronta pra colar no sistema do fornecedor.
const gerarLinhaTexto = (item) => {
  const resp = respostaAprovadaDoItem(item);
  if (!resp) return null;
  const codigo = resp.codigo_fornecedor || item.codigo_fornecedor || item.codigo_global;
  const marca = resp.marca_oferecida || item.marca_solicitada || '(sem marca)';
  const qtd = qtdAprovadaDoItem(item);
  const preco = precoAprovadoDoItem(item) || 0;
  const subtotal = preco * qtd;
  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  return `${codigo} | ${marca} | ${qtd} un. x ${fmt(preco)} = ${fmt(subtotal)}`;
};

// Gera o texto completo da lista pronta para o fornecedor formalizar pedido.
const gerarTextoListaCompleta = (lista) => {
  if (!lista) return '';
  const data = new Date(lista.data_lista).toLocaleDateString('pt-BR');
  const linhas = [];
  linhas.push(`Pedido TAGA Auto Pecas`);
  linhas.push(`Lista #${lista.id} - ${data}`);
  linhas.push(`Fornecedor: ${lista.fornecedor}`);
  linhas.push('');

  let totalGeral = 0;
  let totalItens = 0;
  let totalUnidades = 0;
  let i = 0;
  for (const item of (lista.itens || [])) {
    const linha = gerarLinhaTexto(item);
    if (!linha) continue;
    i += 1;
    totalItens += 1;
    totalUnidades += qtdAprovadaDoItem(item);
    totalGeral += subtotalAprovadoDoItem(item);
    linhas.push(`${String(i).padStart(2, '0')}. ${linha}`);
  }

  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  linhas.push('');
  linhas.push(`TOTAL: ${fmt(totalGeral)}`);
  linhas.push(`Itens: ${totalItens} | Unidades: ${totalUnidades}`);
  if (lista.observacoes_fornecedor) {
    linhas.push('');
    linhas.push(`Observacoes ao fornecedor: ${lista.observacoes_fornecedor}`);
  }
  return linhas.join('\n');
};

// Banner exibido no topo quando a lista esta aprovada/finalizada.
// Mostra mensagem de "siga com a formalizacao" + KPIs do pedido aprovado + botao "copiar lista completa".
function BannerAprovada({ lista }) {
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);
  const itens = lista?.itens || [];

  const resumo = useMemo(() => {
    let totalAprovados = 0;
    let totalUnidades = 0;
    let totalValor = 0;
    let totalFuros = 0;
    for (const item of itens) {
      // Furo só fica fora do pedido quando não há alternativa aprovada. Furo
      // com alternativa aprovada entra normalmente (é a alternativa que vale).
      if (!respostaAprovadaDoItem(item)) {
        if (item.tem_furo_estoque) totalFuros += 1;
        continue;
      }
      totalAprovados += 1;
      totalUnidades += qtdAprovadaDoItem(item);
      totalValor += subtotalAprovadoDoItem(item);
    }
    return { totalAprovados, totalUnidades, totalValor, totalFuros };
  }, [itens]);

  const finalizadoViaWhatsapp = lista?.status_resposta === 'finalizado';

  const copiarTudo = async () => {
    const texto = gerarTextoListaCompleta(lista);
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      toast({ title: "Lista copiada", description: "Cole no seu sistema para formalizar o pedido.", status: "success", duration: 2500 });
      setTimeout(() => setCopiado(false), 2500);
    } catch (e) {
      toast({ title: "Não foi possível copiar automaticamente. Selecione manualmente.", status: "error", duration: 3500 });
    }
  };

  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  return (
    <Card mb={4} boxShadow="lg" bg="green.50" borderLeft="6px solid #16A34A">
      <CardBody>
        <Stack direction={{ base: "column", lg: "row" }} spacing={4} align={{ base: "stretch", lg: "center" }}>
          <Box flex={1}>
            <HStack spacing={2} mb={1} flexWrap="wrap">
              <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                ✅ Lista aprovada pela TAGA
              </Badge>
              {finalizadoViaWhatsapp && (
                <Badge colorScheme="green" variant="outline" fontSize="xs" px={2} py={1}>
                  WhatsApp enviado
                </Badge>
              )}
            </HStack>
            <Heading size="md" color="green.800" mb={1}>
              Pode seguir com a formalização do pedido
            </Heading>
            <Text fontSize="sm" color="green.900">
              As quantidades e preços abaixo são os <strong>definitivos</strong>. Use o botão ao lado para copiar a lista no formato pronto para colar no seu sistema, ou copie linha a linha clicando no botão de cada item.
            </Text>
          </Box>

          <SimpleGrid columns={3} spacing={2} minW={{ lg: "320px" }}>
            <Stat textAlign="center">
              <StatLabel fontSize="xs" color="green.700">Itens aprovados</StatLabel>
              <StatNumber fontSize="xl" color="green.800">{resumo.totalAprovados}</StatNumber>
            </Stat>
            <Stat textAlign="center">
              <StatLabel fontSize="xs" color="green.700">Unidades</StatLabel>
              <StatNumber fontSize="xl" color="green.800">{resumo.totalUnidades}</StatNumber>
            </Stat>
            <Stat textAlign="center">
              <StatLabel fontSize="xs" color="green.700">Valor total</StatLabel>
              <StatNumber fontSize="xl" color="green.800">{fmt(resumo.totalValor)}</StatNumber>
            </Stat>
          </SimpleGrid>

          <Button
            leftIcon={<CopyIcon />}
            colorScheme="green"
            bg={copiado ? "green.600" : "#16A34A"}
            size="lg"
            onClick={copiarTudo}
            minW="220px"
          >
            {copiado ? "Copiado!" : "Copiar lista completa"}
          </Button>
        </Stack>

        {resumo.totalFuros > 0 && (
          <Alert status="warning" mt={3} borderRadius="md" fontSize="xs">
            <AlertIcon />
            {resumo.totalFuros} item(ns) com furo de estoque não entram no pedido final.
          </Alert>
        )}
      </CardBody>
    </Card>
  );
}

// Linha read-only para listas aprovadas/finalizadas.
// Foco: mostrar valores definitivos e facilitar a copia para o sistema do fornecedor.
function ItemLinhaFinalizada({ item, fornecedor }) {
  const toast = useToast();
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();

  const resp = respostaAprovadaDoItem(item);
  const qtdAprovada = qtdAprovadaDoItem(item);
  const precoAprovado = precoAprovadoDoItem(item);
  const subtotal = subtotalAprovadoDoItem(item);
  // Só fica fora do pedido o furo sem alternativa aprovada. Com alternativa
  // aprovada o item entra: o que vale é a alternativa, não o item original.
  const temFuro = !!item.tem_furo_estoque && !resp;
  const furoCoberto = !!item.tem_furo_estoque && !!resp;
  const parcial = resp?.status_aprovacao === 'parcialmente_aprovado';

  const codigoForn = resp?.codigo_fornecedor || item.codigo_fornecedor || item.codigo_global || "";
  const marca = resp?.marca_oferecida || item.marca_solicitada || "(sem marca)";

  const { onCopy: copyCodigoForn, hasCopied: copiedCodigoForn } = useClipboard(codigoForn || "");
  const { onCopy: copyCodigoGlobal, hasCopied: copiedCodigoGlobal } = useClipboard(item.codigo_global || "");

  const linhaTexto = gerarLinhaTexto(item) || "";
  const { onCopy: copyLinha, hasCopied: copiedLinha } = useClipboard(linhaTexto);

  const produtoAdaptado = {
    id: item.produto_id,
    nome: item.nome_produto,
    fornecedor,
    codigo_fornecedor: codigoForn,
    codigo_fabricante: item.codigo_global,
    img: item.img || null,
  };

  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  const corLinha = temFuro
    ? "red.50"
    : (resp ? "green.50" : "gray.50");
  const borderLinha = temFuro
    ? "4px solid #DC2626"
    : (resp ? "4px solid #16A34A" : "4px solid #A0AEC0");

  const onClickCopiarLinha = () => {
    if (!linhaTexto) {
      toast({ title: "Sem dado aprovado para copiar", status: "warning", duration: 2000 });
      return;
    }
    copyLinha();
  };

  return (
    <>
      <Tr bg={corLinha} borderLeft={borderLinha}>
        <Td p={2} verticalAlign="top" w="40px">
          <Text fontSize="sm" fontWeight="bold" color="green.700">
            {temFuro ? "—" : (resp ? "✓" : "—")}
          </Text>
        </Td>

        <Td p={2} verticalAlign="top" display={{ base: "none", md: "table-cell" }}>
          <Box cursor="pointer" onClick={onImageOpen} _hover={{ transform: "scale(1.05)" }} title="Ampliar">
            <ProdutoImagem produto={produtoAdaptado} width="56px" height="56px" imagemAPI={item.img} />
          </Box>
        </Td>

        <Td p={2} verticalAlign="top">
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="bold" color="#01695E">{item.nome_produto}</Text>

            <HStack spacing={3} flexWrap="wrap" fontSize="xs" color="gray.700">
              <Text>Marca: <strong>{marca}</strong></Text>
              {item.codigo_global && (
                <Tooltip label={copiedCodigoGlobal ? "Copiado!" : "Copiar cód. global"}>
                  <Button size="xs" variant="ghost" colorScheme={copiedCodigoGlobal ? "green" : "teal"} leftIcon={<CopyIcon />} onClick={copyCodigoGlobal} px={1} h="auto">
                    {item.codigo_global}
                  </Button>
                </Tooltip>
              )}
              {codigoForn && codigoForn !== item.codigo_global && (
                <Tooltip label={copiedCodigoForn ? "Copiado!" : "Copiar cód. fornecedor"}>
                  <Button size="xs" variant="ghost" colorScheme={copiedCodigoForn ? "green" : "blue"} leftIcon={<CopyIcon />} onClick={copyCodigoForn} px={1} h="auto">
                    {codigoForn}
                  </Button>
                </Tooltip>
              )}
            </HStack>

            {temFuro ? (
              <Box bg="red.100" p={2} borderRadius="md" w="100%" borderLeft="3px solid #DC2626">
                <Text fontSize="xs" fontWeight="bold" color="red.800">
                  🚫 Furo de estoque — não entra no pedido final
                </Text>
              </Box>
            ) : resp ? (
              <Box bg="green.100" p={2} borderRadius="md" w="100%" borderLeft="3px solid #16A34A">
                <HStack justify="space-between" flexWrap="wrap">
                  <Text fontSize="xs" fontWeight="bold" color="green.900">
                    ✅ {parcial ? "APROVADO PARCIAL" : "APROVADO"}: {qtdAprovada} un. × {fmt(precoAprovado)} = <strong>{fmt(subtotal)}</strong>
                  </Text>
                  {parcial && (
                    <Badge colorScheme="orange" fontSize="2xs">qtd menor que a oferecida</Badge>
                  )}
                </HStack>
                {furoCoberto && (
                  <Text fontSize="2xs" color="green.800" mt={1}>
                    Alternativa aprovada no lugar do item declarado em furo — entra no pedido.
                  </Text>
                )}
              </Box>
            ) : (
              <Box bg="gray.100" p={2} borderRadius="md" w="100%">
                <Text fontSize="xs" color="gray.700">Item não aprovado (sem resposta válida)</Text>
              </Box>
            )}
          </VStack>
        </Td>

        <Td p={2} textAlign="center" verticalAlign="middle" w="120px">
          {temFuro || !resp ? (
            <Text fontSize="md" color="gray.500">—</Text>
          ) : (
            <Text fontSize="xl" fontWeight="bold" color="green.700">{qtdAprovada}</Text>
          )}
        </Td>

        <Td p={2} textAlign="right" verticalAlign="middle" w="160px" display={{ base: "none", sm: "table-cell" }}>
          {temFuro || !resp ? (
            <Text fontSize="md" color="gray.500">—</Text>
          ) : (
            <VStack spacing={0} align="end">
              <Text fontSize="md" fontWeight="bold" color="green.700">{fmt(precoAprovado)}</Text>
              <Text fontSize="2xs" color="gray.600">subtotal: <strong>{fmt(subtotal)}</strong></Text>
            </VStack>
          )}
        </Td>

        <Td p={2} textAlign="center" verticalAlign="middle" display={{ base: "none", lg: "table-cell" }}>
          {temFuro ? (
            <Badge colorScheme="red" px={2}>Furo</Badge>
          ) : parcial ? (
            <Badge colorScheme="orange" px={2}>Parcial</Badge>
          ) : furoCoberto ? (
            <Badge colorScheme="blue" px={2}>Alternativa</Badge>
          ) : resp ? (
            <Badge colorScheme="green" px={2}>Aprovado</Badge>
          ) : (
            <Badge colorScheme="gray" px={2}>Sem resposta</Badge>
          )}
        </Td>

        <Td p={2} verticalAlign="middle">
          <Tooltip label={copiedLinha ? "Copiado!" : "Copiar linha completa (código • marca • qtd × preço = subtotal)"}>
            <Button
              size="sm"
              colorScheme={copiedLinha ? "green" : "gray"}
              variant={copiedLinha ? "solid" : "outline"}
              leftIcon={<CopyIcon />}
              onClick={onClickCopiarLinha}
              isDisabled={!resp}
              w="100%"
            >
              {copiedLinha ? "Copiado!" : "Copiar linha"}
            </Button>
          </Tooltip>
        </Td>
      </Tr>

      <Modal isOpen={isImageOpen} onClose={onImageClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw">
          <ModalCloseButton color="white" bg="blackAlpha.600" size="lg" top={2} right={2} />
          <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
            <ProdutoImagem produto={produtoAdaptado} width="auto" height="auto" maxW="100%" maxH="80vh" imagemAPI={item.img} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

function ItemLista({ item, fornecedor, listaFinalizada, onAtualizar, isSelected, onToggleSelect }) {
  const toast = useToast();
  const { isOpen: isAltOpen, onOpen: onAltOpen, onClose: onAltClose } = useDisclosure();
  const { isOpen: isFuroOpen, onOpen: onFuroOpen, onClose: onFuroClose } = useDisclosure();
  const { isOpen: isDivOpen, onOpen: onDivOpen, onClose: onDivClose } = useDisclosure();
  const { isOpen: isImageOpen, onOpen: onImageOpen, onClose: onImageClose } = useDisclosure();

  const divergencias = item.divergencias || [];
  const respostas = item.respostas || [];
  const ajusteQtd = divergencias.find(d => d.tipo === 'quantidade_diferente' && d.status === 'pendente');
  const ajustePreco = divergencias.find(d => d.tipo === 'valor_diferente' && d.status === 'pendente');
  const divergenciasCadastrais = divergencias.filter(
    d => !['quantidade_diferente', 'valor_diferente'].includes(d.tipo)
  );
  const temFuro = !!item.tem_furo_estoque;

  const qtdSolicitada = item.quantidade_solicitada;
  const precoReferencia = item.preco_referencia;

  const qtdAtual = ajusteQtd ? ajusteQtd.quantidade_informada : qtdSolicitada;
  const precoAtual = ajustePreco ? ajustePreco.preco_unitario_informado : precoReferencia;

  const [editQtd, setEditQtd] = useState(qtdAtual != null ? String(qtdAtual) : "");
  const [editPreco, setEditPreco] = useState(precoAtual ? formatarMoedaDecimal(precoAtual) : "");
  const [savingQtd, setSavingQtd] = useState(false);
  const [savingPreco, setSavingPreco] = useState(false);
  const [processandoFuro, setProcessandoFuro] = useState(false);
  const [confirmandoOriginal, setConfirmandoOriginal] = useState(false);
  const [revertendo, setRevertendo] = useState(false);

  useEffect(() => {
    setEditQtd(qtdAtual != null ? String(qtdAtual) : "");
  }, [qtdAtual]);
  useEffect(() => {
    setEditPreco(precoAtual ? formatarMoedaDecimal(precoAtual) : "");
  }, [precoAtual]);

  const [formResposta, setFormResposta] = useState({
    codigo_fornecedor: "",
    marca_oferecida: "",
    quantidade_disponivel: "",
    preco_unitario: "",
    observacoes_fornecedor: ""
  });
  const [respondendo, setRespondendo] = useState(false);

  const [divForm, setDivForm] = useState({
    tipo: 'marca_diferente',
    codigo_fornecedor_informado: '',
    marca_informada: '',
    descricao: '',
  });
  const [enviandoDiv, setEnviandoDiv] = useState(false);

  const { onCopy: copyCodigoForn, hasCopied: copiedCodigoForn } = useClipboard(item.codigo_fornecedor || item.codigo_global || "");
  const { onCopy: copyCodigoGlobal, hasCopied: copiedCodigoGlobal } = useClipboard(item.codigo_global || "");

  const produtoAdaptado = {
    id: item.produto_id,
    nome: item.nome_produto,
    fornecedor,
    codigo_fornecedor: item.codigo_fornecedor,
    codigo_fabricante: item.codigo_global,
    img: item.img || null,
  };

  // ─── Inline edit handlers ────────────────────────────────────────────
  const salvarAjusteQtd = async () => {
    if (listaFinalizada) return;
    const novo = parseInt(editQtd, 10);
    if (isNaN(novo) || novo < 0) {
      setEditQtd(String(qtdAtual ?? ""));
      return;
    }
    if (novo === 0) {
      if (temFuro) return;
      setSavingQtd(true);
      if (ajusteQtd) {
        await removerDivergencia(ajusteQtd.id);
      }
      const r = await declararFuroEstoqueItem(item.id);
      setSavingQtd(false);
      if (r) {
        toast({ title: "Quantidade 0 → furo de estoque informado", status: "warning", duration: 2500 });
        onAtualizar();
      } else {
        toast({ title: "Erro ao informar furo", status: "error", duration: 2500 });
        setEditQtd(String(qtdAtual ?? ""));
      }
      return;
    }
    if (novo === parseInt(qtdSolicitada, 10)) {
      if (ajusteQtd) {
        setSavingQtd(true);
        await removerDivergencia(ajusteQtd.id);
        setSavingQtd(false);
        toast({ title: "Quantidade revertida ao original", status: "info", duration: 1800 });
        onAtualizar();
      }
      return;
    }
    if (ajusteQtd && parseInt(ajusteQtd.quantidade_informada, 10) === novo) return;

    setSavingQtd(true);
    let ok;
    if (ajusteQtd) {
      ok = await atualizarDivergencia(ajusteQtd.id, { quantidade_informada: novo });
    } else {
      ok = await adicionarDivergencia({
        item_lista_compra_id: item.id,
        tipo: 'quantidade_diferente',
        quantidade_informada: novo,
      });
    }
    setSavingQtd(false);
    if (ok) {
      toast({ title: `Ajuste de quantidade enviado (${novo})`, status: "success", duration: 1800 });
      onAtualizar();
    } else {
      toast({ title: "Erro ao salvar quantidade", status: "error", duration: 2500 });
      setEditQtd(String(qtdAtual ?? ""));
    }
  };

  const salvarAjustePreco = async () => {
    if (listaFinalizada) return;
    const decimal = moedaParaDecimal(editPreco);
    if (decimal === null) {
      setEditPreco(precoAtual ? formatarMoedaDecimal(precoAtual) : "");
      return;
    }
    const valorOriginal = precoReferencia != null ? parseFloat(precoReferencia).toFixed(2) : null;
    if (valorOriginal !== null && aproximadamenteIgual(decimal, valorOriginal)) {
      if (ajustePreco) {
        setSavingPreco(true);
        await removerDivergencia(ajustePreco.id);
        setSavingPreco(false);
        toast({ title: "Preço revertido ao original", status: "info", duration: 1800 });
        onAtualizar();
      }
      return;
    }
    if (ajustePreco && aproximadamenteIgual(decimal, ajustePreco.preco_unitario_informado)) return;

    setSavingPreco(true);
    let ok;
    if (ajustePreco) {
      ok = await atualizarDivergencia(ajustePreco.id, { preco_unitario_informado: decimal });
    } else {
      ok = await adicionarDivergencia({
        item_lista_compra_id: item.id,
        tipo: 'valor_diferente',
        preco_unitario_informado: decimal,
      });
    }
    setSavingPreco(false);
    if (ok) {
      toast({ title: `Ajuste de preço enviado (${formatarMoedaDecimal(decimal)})`, status: "success", duration: 1800 });
      onAtualizar();
    } else {
      toast({ title: "Erro ao salvar preço", status: "error", duration: 2500 });
      setEditPreco(precoAtual ? formatarMoedaDecimal(precoAtual) : "");
    }
  };

  // ─── Confirmar item original ─────────────────────────────────────────
  const confirmarItemOriginal = async () => {
    if (listaFinalizada) return;
    if (!item.codigo_fornecedor && !item.codigo_global) {
      toast({ title: "Item sem código de fornecedor", status: "warning", duration: 2500 });
      return;
    }
    setConfirmandoOriginal(true);
    const payload = {
      item_lista_compra_id: item.id,
      codigo_fornecedor: item.codigo_fornecedor || item.codigo_global,
      marca_oferecida: item.marca_solicitada || "(sem marca)",
      quantidade_disponivel: parseInt(qtdAtual, 10),
      preco_unitario: precoAtual ? parseFloat(precoAtual).toFixed(2) : "0.00",
      observacoes_fornecedor: ajusteQtd || ajustePreco
        ? "Confirmado com ajuste de qtd/preço"
        : "Confirmado conforme solicitado",
    };
    const r = await adicionarRespostaItem(payload);
    setConfirmandoOriginal(false);
    if (r) {
      toast({ title: "Item confirmado!", status: "success", duration: 2000 });
      onAtualizar();
    } else {
      toast({ title: "Erro ao confirmar item", status: "error", duration: 2500 });
    }
  };

  // ─── Reverter confirmação ─────────────────────────────────────────────
  const reverterConfirmacao = async () => {
    if (!respostaOriginal) return;
    setRevertendo(true);
    const r = await removerRespostaFornecedor(respostaOriginal.id);
    setRevertendo(false);
    if (r) {
      toast({ title: "Confirmação revertida — você já pode editar quantidade e preço", status: "info", duration: 3000 });
      onAtualizar();
    } else {
      toast({ title: "Não foi possível reverter a confirmação", status: "error", duration: 2500 });
    }
  };

  // ─── Alternativa ──────────────────────────────────────────────────────
  const handleInputAlt = (field, value) => {
    if (field === "preco_unitario") value = formatarMoeda(value);
    else if (field === "quantidade_disponivel") value = value.replace(/\D/g, "");
    setFormResposta(prev => ({ ...prev, [field]: value }));
  };

  const enviarAlternativa = async () => {
    if (!formResposta.codigo_fornecedor || !formResposta.marca_oferecida ||
        !formResposta.quantidade_disponivel || !formResposta.preco_unitario) {
      toast({ title: "Preencha código, marca, quantidade e preço", status: "warning", duration: 2500 });
      return;
    }
    setRespondendo(true);
    const payload = {
      item_lista_compra_id: item.id,
      codigo_fornecedor: formResposta.codigo_fornecedor,
      marca_oferecida: formResposta.marca_oferecida,
      quantidade_disponivel: parseInt(formResposta.quantidade_disponivel),
      preco_unitario: moedaParaDecimal(formResposta.preco_unitario),
      observacoes_fornecedor: formResposta.observacoes_fornecedor,
    };
    const r = await adicionarRespostaItem(payload);
    setRespondendo(false);
    if (r) {
      toast({ title: "Alternativa adicionada!", status: "success", duration: 2000 });
      setFormResposta({
        codigo_fornecedor: "",
        marca_oferecida: "",
        quantidade_disponivel: "",
        preco_unitario: "",
        observacoes_fornecedor: ""
      });
      onAltClose();
      onAtualizar();
    } else {
      toast({ title: "Erro ao adicionar alternativa", status: "error", duration: 2500 });
    }
  };

  // ─── Furo ─────────────────────────────────────────────────────────────
  const confirmarFuro = async () => {
    setProcessandoFuro(true);
    const r = await declararFuroEstoqueItem(item.id);
    setProcessandoFuro(false);
    if (r) {
      toast({ title: "Furo de estoque informado", status: "warning", duration: 2500 });
      onFuroClose();
      onAtualizar();
    } else {
      toast({ title: "Erro ao informar furo", status: "error", duration: 2500 });
    }
  };

  const desfazerFuro = async () => {
    setProcessandoFuro(true);
    const r = await desfazerFuroEstoqueItem(item.id);
    setProcessandoFuro(false);
    if (r) {
      toast({ title: "Furo desfeito", status: "info", duration: 2000 });
      onAtualizar();
    } else {
      toast({ title: "Erro ao desfazer furo", status: "error", duration: 2500 });
    }
  };

  // ─── Divergência cadastral ────────────────────────────────────────────
  const enviarDivergenciaCadastral = async () => {
    const codigo = (divForm.codigo_fornecedor_informado || '').trim();
    const marca = (divForm.marca_informada || '').trim();
    const descricao = (divForm.descricao || '').trim();
    if (!codigo && !marca && !descricao) {
      toast({ title: "Preencha código, marca ou descrição", status: "warning", duration: 2500 });
      return;
    }
    setEnviandoDiv(true);
    const payload = { item_lista_compra_id: item.id, tipo: divForm.tipo };
    if (codigo) payload.codigo_fornecedor_informado = codigo;
    if (marca) payload.marca_informada = marca;
    if (descricao) payload.descricao = descricao;
    const r = await adicionarDivergencia(payload);
    setEnviandoDiv(false);
    if (r) {
      toast({ title: "Divergência registrada", status: "success", duration: 2000 });
      setDivForm({ tipo: 'marca_diferente', codigo_fornecedor_informado: '', marca_informada: '', descricao: '' });
      onDivClose();
      onAtualizar();
    } else {
      toast({ title: "Erro ao registrar divergência", status: "error", duration: 2500 });
    }
  };

  const removerDivergenciaItem = async (divergenciaId) => {
    const r = await removerDivergencia(divergenciaId);
    if (r) {
      toast({ title: "Divergência removida", status: "info", duration: 1500 });
      onAtualizar();
    } else {
      toast({ title: "Não foi possível remover (pode já ter sido tratada)", status: "error", duration: 2500 });
    }
  };

  // ─── Status visual ────────────────────────────────────────────────────
  const codigoEsperado = item.codigo_fornecedor || item.codigo_global;
  const marcaEsperada = (item.marca_solicitada || '(sem marca)').toLowerCase();
  const respostaOriginal = respostas.find(r =>
    r.codigo_fornecedor === codigoEsperado &&
    (r.marca_oferecida || '').toLowerCase() === marcaEsperada
  );
  const itemConfirmado = !!respostaOriginal;
  const totalAlt = respostas.filter(r => r.id !== respostaOriginal?.id).length;
  const temAjuste = !!ajusteQtd || !!ajustePreco;

  const statusBadge = () => {
    if (temFuro) return <Badge colorScheme="red" px={2}>Sem estoque</Badge>;
    if (itemConfirmado && totalAlt > 0) return <Badge colorScheme="green" px={2}>Conf. + {totalAlt} alt.</Badge>;
    if (itemConfirmado) return <Badge colorScheme="green" px={2}>Confirmado</Badge>;
    if (totalAlt > 0) return <Badge colorScheme="blue" px={2}>{totalAlt} alternativa(s)</Badge>;
    if (temAjuste) return <Badge colorScheme="orange" px={2}>Ajustado</Badge>;
    if (divergenciasCadastrais.length > 0) return <Badge colorScheme="yellow" px={2}>Divergência</Badge>;
    return <Badge colorScheme="gray" px={2}>Aguardando</Badge>;
  };

  const corLinha = temFuro
    ? "red.50"
    : itemConfirmado
      ? "green.50"
      : temAjuste
        ? "orange.50"
        : divergenciasCadastrais.length > 0
          ? "yellow.50"
          : item.urgente ? "#FFF5F0" : "white";

  const borderLinha = temFuro
    ? "4px solid #DC2626"
    : itemConfirmado
      ? "4px solid #16A34A"
      : temAjuste
        ? "4px solid #F97316"
        : divergenciasCadastrais.length > 0
          ? "4px solid #EAB308"
          : item.urgente ? "4px solid #F68633" : "none";

  return (
    <>
      <Tr bg={corLinha} borderLeft={borderLinha} _hover={{ filter: "brightness(0.98)" }}>
        <Td p={2} verticalAlign="top">
          <Checkbox
            isChecked={isSelected}
            onChange={() => onToggleSelect(item.id)}
            colorScheme="green"
            isDisabled={listaFinalizada || temFuro || itemConfirmado}
          />
        </Td>

        <Td p={2} verticalAlign="top" display={{ base: "none", md: "table-cell" }}>
          <Box cursor="pointer" onClick={onImageOpen} _hover={{ transform: "scale(1.05)" }} title="Ampliar">
            <ProdutoImagem produto={produtoAdaptado} width="56px" height="56px" imagemAPI={item.img} />
          </Box>
        </Td>

        <Td p={2} verticalAlign="top">
          <VStack align="start" spacing={1}>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontSize="sm" fontWeight="bold" color="#01695E">{item.nome_produto}</Text>
              {item.urgente && <Badge colorScheme="orange" variant="solid">Urgente</Badge>}
            </HStack>

            <HStack spacing={3} flexWrap="wrap" fontSize="xs" color="gray.700">
              {item.marca_solicitada && (
                <Text>Marca: <strong>{item.marca_solicitada}</strong></Text>
              )}
              {item.codigo_global && (
                <Tooltip label={copiedCodigoGlobal ? "Copiado!" : "Copiar cód. global"}>
                  <Button size="xs" variant="ghost" colorScheme={copiedCodigoGlobal ? "green" : "teal"} leftIcon={<CopyIcon />} onClick={copyCodigoGlobal} px={1} h="auto">
                    {item.codigo_global}
                  </Button>
                </Tooltip>
              )}
              {(item.codigo_fornecedor || item.codigo_global) && item.codigo_fornecedor !== item.codigo_global && (
                <Tooltip label={copiedCodigoForn ? "Copiado!" : "Copiar cód. fornecedor"}>
                  <Button size="xs" variant="ghost" colorScheme={copiedCodigoForn ? "green" : "blue"} leftIcon={<CopyIcon />} onClick={copyCodigoForn} px={1} h="auto">
                    {item.codigo_fornecedor || item.codigo_global}
                  </Button>
                </Tooltip>
              )}
            </HStack>

            {item.observacoes && (
              <Text fontSize="xs" color="blue.700" bg="blue.50" px={2} py={1} borderRadius="sm">
                📝 {item.observacoes}
              </Text>
            )}

            {/* Alternativas existentes */}
            {totalAlt > 0 && (
              <Box bg="blue.50" p={2} borderRadius="md" w="100%" borderLeft="3px solid #3182CE">
                <Text fontSize="xs" fontWeight="bold" color="blue.800" mb={1}>
                  🔁 Alternativas enviadas ({totalAlt})
                </Text>
                {respostas.filter(r => r.id !== respostaOriginal?.id).map((r) => (
                  <HStack key={r.id} spacing={2} fontSize="xs" color="blue.900">
                    <Text>•</Text>
                    <Text><strong>{r.marca_oferecida}</strong></Text>
                    <Text>•</Text>
                    <Text>{r.quantidade_disponivel} un.</Text>
                    <Text>•</Text>
                    <Text fontWeight="bold">{formatarMoedaDecimal(r.preco_unitario)}</Text>
                    {r.status_aprovacao === 'aprovado' && <Badge colorScheme="green">aprovada</Badge>}
                    {r.status_aprovacao === 'rejeitado' && <Badge colorScheme="red">rejeitada</Badge>}
                  </HStack>
                ))}
              </Box>
            )}

            {/* Item confirmado */}
            {itemConfirmado && (
              <Box bg="green.50" p={2} borderRadius="md" w="100%" borderLeft="3px solid #16A34A">
                <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
                  <Text fontSize="xs" fontWeight="bold" color="green.800">
                    ✓ Item confirmado: {respostaOriginal.quantidade_disponivel} un. por {formatarMoedaDecimal(respostaOriginal.preco_unitario)}
                  </Text>
                  {!listaFinalizada && respostaOriginal.status_aprovacao === 'pendente' && (
                    <Button size="xs" colorScheme="orange" variant="outline" leftIcon={<RepeatIcon />}
                      onClick={reverterConfirmacao} isLoading={revertendo} loadingText="Revertendo...">
                      Reverter confirmação
                    </Button>
                  )}
                </Flex>
              </Box>
            )}

            {/* Divergências cadastrais */}
            {divergenciasCadastrais.length > 0 && (
              <Box bg="yellow.50" p={2} borderRadius="md" w="100%" borderLeft="3px solid #EAB308">
                <Text fontSize="xs" fontWeight="bold" color="yellow.800" mb={1}>
                  ⚠ Divergência cadastral
                </Text>
                {divergenciasCadastrais.map((d) => (
                  <HStack key={d.id} spacing={2} align="start" mb={1}>
                    <Box flex={1}>
                      <Text fontSize="xs" color="yellow.900">
                        <strong>{tipoDivergenciaLabel(d.tipo)}</strong>
                        {d.codigo_fornecedor_informado && ` — Cód: ${d.codigo_fornecedor_informado}`}
                        {d.marca_informada && ` — Marca: ${d.marca_informada}`}
                      </Text>
                      {d.descricao && <Text fontSize="xs" color="yellow.900">{d.descricao}</Text>}
                      <Badge size="sm" fontSize="2xs"
                        colorScheme={d.status === 'pendente' ? 'gray' : d.status === 'reconhecida' ? 'green' : 'red'}>
                        {d.status}
                      </Badge>
                    </Box>
                    {d.status === 'pendente' && !listaFinalizada && (
                      <IconButton icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
                        onClick={() => removerDivergenciaItem(d.id)} aria-label="Remover" />
                    )}
                  </HStack>
                ))}
              </Box>
            )}

            {/* Furo declarado */}
            {temFuro && (
              <Box bg="red.50" p={2} borderRadius="md" w="100%" borderLeft="3px solid #DC2626">
                <Text fontSize="xs" fontWeight="bold" color="red.800">
                  🚫 Furo de estoque declarado{item.furo_aceito_em && " (aceito pelo comprador)"}
                </Text>
              </Box>
            )}
          </VStack>
        </Td>

        {/* Quantidade editável */}
        <Td p={2} textAlign="center" verticalAlign="top" w="120px">
          <VStack spacing={1}>
            <NumberInput
              value={editQtd}
              onChange={(s) => setEditQtd(s)}
              onBlur={salvarAjusteQtd}
              isDisabled={listaFinalizada || temFuro || itemConfirmado || savingQtd}
              min={0}
              size="sm"
              w="90px"
              precision={0}
            >
              <NumberInputField
                textAlign="center"
                fontWeight="bold"
                fontSize="md"
                color={ajusteQtd ? "orange.700" : "#F68633"}
                bg={ajusteQtd ? "orange.50" : "white"}
                borderColor={ajusteQtd ? "orange.400" : "gray.300"}
                _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              />
            </NumberInput>
            {ajusteQtd && (
              <Text fontSize="2xs" color="orange.600" fontWeight="bold">
                pedido: {qtdSolicitada}
              </Text>
            )}
          </VStack>
        </Td>

        {/* Preço editável */}
        <Td p={2} textAlign="right" verticalAlign="top" w="160px" display={{ base: "none", sm: "table-cell" }}>
          <VStack spacing={1} align="end">
            <Input
              value={editPreco}
              onChange={(e) => setEditPreco(formatarMoeda(e.target.value))}
              onBlur={salvarAjustePreco}
              isDisabled={listaFinalizada || temFuro || itemConfirmado || savingPreco}
              size="sm"
              w="130px"
              textAlign="right"
              fontWeight="bold"
              color={ajustePreco ? "orange.700" : "gray.800"}
              bg={ajustePreco ? "orange.50" : "white"}
              borderColor={ajustePreco ? "orange.400" : "gray.300"}
              _focus={{ borderColor: "#F68633", boxShadow: "0 0 0 1px #F68633" }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              placeholder="R$ 0,00"
            />
            {ajustePreco && (
              <Text fontSize="2xs" color="orange.600" fontWeight="bold">
                pedido: {formatarMoedaDecimal(precoReferencia)}
              </Text>
            )}
            {item.preco_maximo && (
              <Text fontSize="2xs" color="red.500">
                máx: {formatarMoedaDecimal(item.preco_maximo)}
              </Text>
            )}
          </VStack>
        </Td>

        <Td p={2} textAlign="center" verticalAlign="top" display={{ base: "none", lg: "table-cell" }}>
          {statusBadge()}
        </Td>

        <Td p={2} verticalAlign="top">
          <Wrap spacing={1} justify="center">
            <WrapItem>
              <Tooltip label={itemConfirmado ? "Já confirmado" : "Confirmar este item"}>
                <IconButton icon={<CheckIcon />} size="sm" colorScheme="green"
                  variant={itemConfirmado ? "solid" : "outline"}
                  onClick={confirmarItemOriginal}
                  isLoading={confirmandoOriginal}
                  isDisabled={listaFinalizada || temFuro || itemConfirmado}
                  aria-label="Confirmar" />
              </Tooltip>
            </WrapItem>
            <WrapItem>
              <Tooltip label="Adicionar alternativa">
                <IconButton icon={<AddIcon />} size="sm" colorScheme="blue" variant="outline"
                  onClick={onAltOpen} isDisabled={listaFinalizada}
                  aria-label="Alternativa" />
              </Tooltip>
            </WrapItem>
            <WrapItem>
              {temFuro ? (
                <Tooltip label="Desfazer furo">
                  <IconButton icon={<CloseIcon />} size="sm" colorScheme="red" variant="solid"
                    onClick={desfazerFuro} isDisabled={listaFinalizada} isLoading={processandoFuro}
                    aria-label="Desfazer furo" />
                </Tooltip>
              ) : (
                <Tooltip label="Sem estoque">
                  <IconButton icon={<WarningIcon />} size="sm" colorScheme="red" variant="outline"
                    onClick={onFuroOpen} isDisabled={listaFinalizada}
                    aria-label="Sem estoque" />
                </Tooltip>
              )}
            </WrapItem>
            <WrapItem>
              <Tooltip label="Outras divergências (foto / marca / código)">
                <IconButton icon={<EditIcon />} size="sm" colorScheme="yellow" variant="outline"
                  onClick={onDivOpen} isDisabled={listaFinalizada}
                  aria-label="Divergência cadastral" />
              </Tooltip>
            </WrapItem>
          </Wrap>
        </Td>
      </Tr>

      {/* Modal de imagem */}
      <Modal isOpen={isImageOpen} onClose={onImageClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw">
          <ModalCloseButton color="white" bg="blackAlpha.600" size="lg" top={2} right={2} />
          <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
            <ProdutoImagem produto={produtoAdaptado} width="auto" height="auto" maxW="100%" maxH="80vh" imagemAPI={item.img} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal Alternativa */}
      <Modal isOpen={isAltOpen} onClose={onAltClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="blue.700">
            <HStack><AddIcon /><Text>Adicionar Alternativa</Text></HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md" mb={3} fontSize="xs">
              <AlertIcon />
              Use isto para sugerir um produto similar/equivalente. Para confirmar o item original, use o botão verde ✓ na linha.
            </Alert>
            <Stack spacing={3}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="xs">Código Fornecedor</FormLabel>
                  <Input value={formResposta.codigo_fornecedor}
                    onChange={(e) => handleInputAlt("codigo_fornecedor", e.target.value)}
                    placeholder="Ex.: ABC-123" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="xs">Marca Oferecida</FormLabel>
                  <Input value={formResposta.marca_oferecida}
                    onChange={(e) => handleInputAlt("marca_oferecida", e.target.value)}
                    placeholder="Ex.: Bosch" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="xs">Quantidade</FormLabel>
                  <Input value={formResposta.quantidade_disponivel}
                    onChange={(e) => handleInputAlt("quantidade_disponivel", e.target.value)}
                    placeholder="0" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="xs">Preço Unitário</FormLabel>
                  <Input value={formResposta.preco_unitario}
                    onChange={(e) => handleInputAlt("preco_unitario", e.target.value)}
                    placeholder="R$ 0,00" />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="xs">Observações (opcional)</FormLabel>
                <Textarea value={formResposta.observacoes_fornecedor}
                  onChange={(e) => handleInputAlt("observacoes_fornecedor", e.target.value)}
                  rows={2} placeholder="Ex.: Pronto para entrega imediata" />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onAltClose}>Cancelar</Button>
            <Button colorScheme="blue" leftIcon={<CheckIcon />}
              onClick={enviarAlternativa} isLoading={respondendo} loadingText="Enviando">
              Adicionar Alternativa
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Furo */}
      <Modal isOpen={isFuroOpen} onClose={onFuroClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="red.700">
            <HStack><WarningIcon /><Text>Informar furo de estoque</Text></HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={3}>
              <Text fontSize="sm">Você está prestes a informar que <strong>não tem este produto em estoque</strong>:</Text>
              <Box bg="gray.50" p={3} borderRadius="md" w="100%">
                <Text fontSize="sm" fontWeight="bold">{item.nome_produto}</Text>
                <Text fontSize="xs" color="gray.600">
                  {item.marca_solicitada && <>Marca: {item.marca_solicitada} • </>}
                  Cód: {item.codigo_global} • Qtd: {qtdSolicitada}
                </Text>
              </Box>
              <Alert status="info" borderRadius="md" fontSize="xs">
                <AlertIcon />
                Você pode desfazer enquanto a lista não for finalizada. Alternativas adicionadas continuam ativas.
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onFuroClose}>Cancelar</Button>
            <Button colorScheme="red" onClick={confirmarFuro} isLoading={processandoFuro}>
              Confirmar furo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Divergência cadastral */}
      <Modal isOpen={isDivOpen} onClose={onDivClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="yellow.700">
            <HStack><EditIcon /><Text>Outras divergências</Text></HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="info" borderRadius="md" mb={3} fontSize="xs">
              <AlertIcon />
              Use para reportar erros cadastrais (foto, marca, código, descrição). Quantidade e preço são editados direto na linha do item.
            </Alert>
            <Stack spacing={3}>
              <FormControl isRequired>
                <FormLabel fontSize="xs">Tipo</FormLabel>
                <Select value={divForm.tipo}
                  onChange={(e) => setDivForm(p => ({ ...p, tipo: e.target.value }))}>
                  {TIPOS_DIVERGENCIA_CADASTRAL.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs">Código fornecedor correto</FormLabel>
                  <Input value={divForm.codigo_fornecedor_informado}
                    onChange={(e) => setDivForm(p => ({ ...p, codigo_fornecedor_informado: e.target.value }))}
                    placeholder={item.codigo_fornecedor || item.codigo_global || ""} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Marca correta</FormLabel>
                  <Input value={divForm.marca_informada}
                    onChange={(e) => setDivForm(p => ({ ...p, marca_informada: e.target.value }))}
                    placeholder={item.marca_solicitada || ""} />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="xs">Detalhes (opcional)</FormLabel>
                <Textarea value={divForm.descricao}
                  onChange={(e) => setDivForm(p => ({ ...p, descricao: e.target.value }))}
                  rows={2} placeholder="Ex.: A foto cadastrada não é deste produto" />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onDivClose}>Cancelar</Button>
            <Button colorScheme="yellow" leftIcon={<CheckIcon />}
              onClick={enviarDivergenciaCadastral} isLoading={enviandoDiv} loadingText="Enviando">
              Registrar divergência
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// ─── Componente principal ───────────────────────────────────────────────
// Fornecedor registra o nº do pedido formalizado no sistema dele — ao salvar,
// a equipe TAGA é avisada via WhatsApp (números configurados nas Configurações)
function CardPedidoFormalizado({ lista, hash, onAtualizar }) {
  const toast = useToast();
  const [editando, setEditando] = useState(!lista.numero_pedido_formalizado);
  const [numero, setNumero] = useState(lista.numero_pedido_formalizado || "");
  const [salvando, setSalvando] = useState(false);

  const handleSalvar = async () => {
    const valor = numero.trim();
    if (!valor) {
      toast({ title: "Informe o número do pedido", status: "warning", duration: 2500 });
      return;
    }
    if (valor === lista.numero_pedido_formalizado) {
      setEditando(false);
      return;
    }
    setSalvando(true);
    const r = await informarPedidoFormalizado(hash, valor);
    setSalvando(false);
    if (r) {
      setEditando(false);
      toast({
        title: "Número do pedido registrado",
        description: r.notificados > 0 ? "A equipe TAGA foi avisada. Obrigado!" : undefined,
        status: "success",
        duration: 4000,
      });
      onAtualizar();
    } else {
      toast({ title: "Erro ao registrar o número do pedido", status: "error", duration: 4000 });
    }
  };

  if (!editando && lista.numero_pedido_formalizado) {
    return (
      <Card mb={4} boxShadow="md" borderLeft="4px solid #16A34A">
        <CardBody py={3}>
          <Flex direction={{ base: "column", sm: "row" }} gap={2} align={{ sm: "center" }}>
            <HStack spacing={2} flexWrap="wrap">
              <CheckIcon color="green.500" />
              <Text fontWeight="bold" color="#01695E">Pedido formalizado:</Text>
              <Badge colorScheme="green" fontSize="md" px={2}>{lista.numero_pedido_formalizado}</Badge>
              {lista.pedido_formalizado_em && (
                <Text fontSize="xs" color="gray.500">
                  em {new Date(lista.pedido_formalizado_em).toLocaleString('pt-BR')}
                </Text>
              )}
            </HStack>
            <Box flex={1} />
            <Button size="xs" variant="outline" leftIcon={<EditIcon />} onClick={() => setEditando(true)}>
              Alterar
            </Button>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card mb={4} boxShadow="md" borderLeft="4px solid #F68633">
      <CardBody py={3}>
        <Text fontWeight="bold" color="#01695E" mb={1}>
          Já formalizou o pedido no seu sistema?
        </Text>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Informe abaixo o número do pedido — a equipe TAGA será avisada automaticamente.
        </Text>
        <Flex direction={{ base: "column", sm: "row" }} gap={2} maxW="520px">
          <Input
            size="sm"
            placeholder="Nº do pedido (ex: 123456)"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSalvar(); }}
            bg="white"
          />
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="green"
              bg="#16A34A"
              leftIcon={<CheckIcon />}
              onClick={handleSalvar}
              isLoading={salvando}
              loadingText="Salvando..."
              flexShrink={0}
            >
              Salvar e avisar TAGA
            </Button>
            {!!lista.numero_pedido_formalizado && (
              <Button size="sm" variant="ghost" onClick={() => {
                setNumero(lista.numero_pedido_formalizado);
                setEditando(false);
              }}>
                Cancelar
              </Button>
            )}
          </HStack>
        </Flex>
      </CardBody>
    </Card>
  );
}

export default function ListaComprasFornecedor() {
  const { hash } = useParams();
  const [loading, setLoading] = useState(true);
  const [lista, setLista] = useState(null);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const [processandoLote, setProcessandoLote] = useState(false);
  const toast = useToast();

  const fetchLista = async () => {
    const data = await buscarListaComprasPorHash(hash);
    if (data) setLista(data);
    else toast({ title: "Lista não encontrada", status: "error", duration: 4000 });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchLista();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  const listaFinalizada = !!lista?.finalizada;

  const isItemConfirmavel = (item) => {
    if (item.tem_furo_estoque) return false;
    const codigoEsperado = item.codigo_fornecedor || item.codigo_global;
    const marcaEsperada = (item.marca_solicitada || '(sem marca)').toLowerCase();
    const jaConfirmado = (item.respostas || []).some(r =>
      r.codigo_fornecedor === codigoEsperado &&
      (r.marca_oferecida || '').toLowerCase() === marcaEsperada
    );
    return !jaConfirmado;
  };

  const handleToggleSelect = (id) =>
    setItensSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const itensConfirmaveis = useMemo(
    () => (lista?.itens || []).filter(isItemConfirmavel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lista]
  );

  const handleSelecionarTodos = () => {
    if (itensSelecionados.length === itensConfirmaveis.length) {
      setItensSelecionados([]);
    } else {
      setItensSelecionados(itensConfirmaveis.map(i => i.id));
    }
  };

  const handleConfirmarSelecionados = async () => {
    if (itensSelecionados.length === 0) {
      toast({ title: "Selecione ao menos um item", status: "warning", duration: 2500 });
      return;
    }
    setProcessandoLote(true);
    let ok = 0, erro = 0;
    for (const id of itensSelecionados) {
      const item = lista.itens.find(i => i.id === id);
      if (!item) continue;
      const ajusteQtd = item.divergencias?.find(d => d.tipo === 'quantidade_diferente' && d.status === 'pendente');
      const ajustePreco = item.divergencias?.find(d => d.tipo === 'valor_diferente' && d.status === 'pendente');
      const qtdAtual = ajusteQtd ? ajusteQtd.quantidade_informada : item.quantidade_solicitada;
      const precoAtual = ajustePreco ? ajustePreco.preco_unitario_informado : item.preco_referencia;
      if (!item.codigo_fornecedor && !item.codigo_global) { erro++; continue; }
      if (precoAtual == null) { erro++; continue; }
      const payload = {
        item_lista_compra_id: item.id,
        codigo_fornecedor: item.codigo_fornecedor || item.codigo_global,
        marca_oferecida: item.marca_solicitada || "(sem marca)",
        quantidade_disponivel: parseInt(qtdAtual, 10),
        preco_unitario: parseFloat(precoAtual).toFixed(2),
        observacoes_fornecedor: ajusteQtd || ajustePreco
          ? "Confirmado em lote com ajuste"
          : "Confirmado em lote",
      };
      const r = await adicionarRespostaItem(payload);
      if (r) ok++; else erro++;
    }
    setProcessandoLote(false);
    setItensSelecionados([]);
    toast({
      title: `Confirmados em lote: ${ok}${erro ? ` • erros: ${erro}` : ''}`,
      status: ok > 0 ? "success" : "error",
      duration: 4000,
    });
    fetchLista();
  };

  // KPIs
  const kpis = useMemo(() => {
    const itens = lista?.itens || [];
    const respondidos = itens.filter(i => (i.respostas?.length || 0) > 0 || i.tem_furo_estoque).length;
    const comAjuste = itens.filter(i => (i.divergencias || []).some(
      d => ['valor_diferente', 'quantidade_diferente'].includes(d.tipo) && d.status === 'pendente')).length;
    const comAlternativa = itens.filter(i => (i.respostas?.length || 0) > 0).length;
    const comDivergencia = itens.filter(i => (i.divergencias || []).some(
      d => !['valor_diferente', 'quantidade_diferente'].includes(d.tipo) && d.status === 'pendente')).length;
    const furos = itens.filter(i => i.tem_furo_estoque).length;
    return { total: itens.length, respondidos, comAjuste, comAlternativa, comDivergencia, furos };
  }, [lista]);

  if (loading) {
    return (
      <Box minH="100vh" display="flex" justifyContent="center" alignItems="center" bg="#f0f0f0">
        <VStack spacing={4}>
          <Spinner size="xl" color="#F68633" thickness="4px" />
          <Text color="#01695E">Carregando lista de compras...</Text>
        </VStack>
      </Box>
    );
  }

  if (!lista) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Lista não encontrada</AlertTitle>
            <AlertDescription>O link pode estar incorreto ou expirado.</AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg="#f0f0f0" py={4}>
      <Container maxW="container.xl">
        {/* Header */}
        <Card mb={4} boxShadow="lg" bg="#F68633" color="white">
          <CardBody>
            <VStack spacing={2} align="start">
              <HStack spacing={3} flexWrap="wrap">
                <Heading size="lg">Lista de compras — {lista.fornecedor}</Heading>
                <Badge bg="whiteAlpha.300" color="white" fontSize="md" px={3} py={1}>
                  {new Date(lista.data_lista).toLocaleDateString('pt-BR')}
                </Badge>
                {listaFinalizada && (
                  <Badge bg="white" color="green.700" fontSize="md" px={3} py={1}>Finalizada</Badge>
                )}
              </HStack>
              {lista.observacoes && (
                <Box bg="whiteAlpha.300" p={3} borderRadius="md" w="100%">
                  <Text fontSize="sm" fontWeight="bold">Observações da TAGA:</Text>
                  <Text fontSize="sm">{lista.observacoes}</Text>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Nº do pedido formalizado no sistema do fornecedor */}
        <CardPedidoFormalizado lista={lista} hash={hash} onAtualizar={fetchLista} />

        {/* KPIs do estado em andamento — escondidos quando a lista foi finalizada
            (o BannerAprovada ja resume itens aprovados, unidades e valor total). */}
        {!listaFinalizada && (
          <Card mb={4} boxShadow="md">
            <CardBody py={3}>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 6 }} spacing={3}>
                <Stat>
                  <StatLabel fontSize="xs" color="gray.600">Total</StatLabel>
                  <StatNumber fontSize="2xl" color="#01695E">{kpis.total}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs" color="gray.600">Respondidos</StatLabel>
                  <StatNumber fontSize="2xl" color="green.600">{kpis.respondidos}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs" color="gray.600">Com ajuste</StatLabel>
                  <StatNumber fontSize="2xl" color="orange.500">{kpis.comAjuste}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs" color="gray.600">Alternativas</StatLabel>
                  <StatNumber fontSize="2xl" color="blue.500">{kpis.comAlternativa}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs" color="gray.600">Divergências</StatLabel>
                  <StatNumber fontSize="2xl" color="yellow.600">{kpis.comDivergencia}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs" color="gray.600">Furos</StatLabel>
                  <StatNumber fontSize="2xl" color="red.500">{kpis.furos}</StatNumber>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {listaFinalizada && (
          <BannerAprovada lista={lista} />
        )}

        {/* Toolbar de ação em lote */}
        {!listaFinalizada && itensConfirmaveis.length > 0 && (
          <Card mb={4} boxShadow="sm" bg="white" borderTop="3px solid #16A34A">
            <CardBody py={3}>
              <Flex direction={{ base: "column", md: "row" }} gap={3} align="center">
                <Checkbox
                  isChecked={itensConfirmaveis.length > 0 && itensSelecionados.length === itensConfirmaveis.length}
                  onChange={handleSelecionarTodos}
                  colorScheme="green"
                >
                  <Text fontSize="sm" fontWeight="bold">
                    Selecionar todos ainda não confirmados ({itensConfirmaveis.length})
                  </Text>
                </Checkbox>
                {itensSelecionados.length > 0 && (
                  <Badge colorScheme="green" fontSize="sm">{itensSelecionados.length} selecionado(s)</Badge>
                )}
                <Box flex={1} />
                <Button
                  colorScheme="green" bg="#16A34A" leftIcon={<CheckIcon />}
                  onClick={handleConfirmarSelecionados}
                  isLoading={processandoLote} loadingText="Confirmando..."
                  isDisabled={itensSelecionados.length === 0}
                  size="md"
                >
                  Confirmar selecionados
                </Button>
              </Flex>
              <Text fontSize="xs" color="gray.600" mt={2}>
                💡 Edite quantidade e preço direto na linha e clique no ✓ verde para confirmar o item. O comprador só vê e aprova os valores depois da sua confirmação.
              </Text>
            </CardBody>
          </Card>
        )}

        {/* Tabela de itens */}
        {lista.itens?.length > 0 ? (
          <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="lg" pb={2}>
            <Table variant="simple" size="sm">
              <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th w="40px" p={2}></Th>
                  <Th w="70px" p={2} display={{ base: "none", md: "table-cell" }}>Produto</Th>
                  <Th p={2}>Descrição</Th>
                  <Th textAlign="center" w="120px" p={2}>Quantidade</Th>
                  <Th textAlign="right" w="160px" p={2} display={{ base: "none", sm: "table-cell" }}>Preço unit.</Th>
                  <Th textAlign="center" w="120px" p={2} display={{ base: "none", lg: "table-cell" }}>Status</Th>
                  <Th textAlign="center" w="180px" p={2}>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {lista.itens.map(item => (
                  listaFinalizada ? (
                    <ItemLinhaFinalizada
                      key={item.id}
                      item={item}
                      fornecedor={lista.fornecedor}
                    />
                  ) : (
                    <ItemLista
                      key={item.id}
                      item={item}
                      fornecedor={lista.fornecedor}
                      listaFinalizada={listaFinalizada}
                      onAtualizar={fetchLista}
                      isSelected={itensSelecionados.includes(item.id)}
                      onToggleSelect={handleToggleSelect}
                    />
                  )
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Alert status="info" borderRadius="md">
            <AlertIcon /> Nenhum item na lista.
          </Alert>
        )}

        <Divider my={6} />
        <Box textAlign="center" color="gray.600" pb={4}>
          <Text fontSize="sm">© {new Date().getFullYear()} TAGA Auto Peças — Sistema de lista de compras</Text>
        </Box>
      </Container>
    </Box>
  );
}
